import { useState, useEffect, useRef, useMemo } from "react";
import { DndProvider, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { fireSwal } from "utils/swalConfig";
import { checkSubscriptionEligibility } from "api";
import { useClassroomManager } from "hooks/useClassroomManager";

import { FaTrash, FaSchool, FaUsers, FaInfoCircle, FaStar } from "react-icons/fa";
import { FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { MdGroup, MdErrorOutline } from "react-icons/md";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { DraggableStudentCard } from "utils/dragndropConfig";
import { IoPersonAddOutline, IoPersonRemoveOutline, IoSearchOutline, IoAlertCircle, IoShieldCheckmarkOutline } from "react-icons/io5";
import InputField from "components/fields/InputField";
import TextField from "components/fields/TextField";

const roles = [
    "Team Leader",
    "Design Specialist",
    "Technical Specialist",
    "Research & Development Specialist",
    "Documentation & Presentation Specialist",
];
const ItemTypes = { STUDENT: 'student' };
const MIN_MEMBERS = 3;
const MAX_MEMBERS = 5;

const DroppableTeamCard = ({ team, onDropStudent }) => {
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.STUDENT,
        drop: (item) => onDropStudent(item.student.id, team.id),
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
        }),
    }));

    return (
        <div ref={drop} className={`p-6 bg-gray-50 dark:bg-navy-800 rounded-xl space-y-4 border-2 border-dashed transition-all ${isOver && canDrop ? 'border-brand-500 bg-brand-500/10' : 'border-gray-300 dark:border-navy-600'}`}>
            <div className="flex justify-between items-start">
                <div>
                    <h4 className="font-bold text-xl text-navy-700 dark:text-white">{team.name}</h4>
                    <p className="text-base text-gray-500 dark:text-gray-400 flex items-center gap-2 mt-1"><FaInfoCircle /> {team.description || "No description."}</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-400/20 text-yellow-500 rounded-full font-bold text-base">
                    <FaStar /><span>{team.points ?? 0} points</span>
                </div>
            </div>
            <div className="min-h-[60px] space-y-2 bg-gray-100 dark:bg-navy-900/50 p-3 rounded-md">
                <h5 className="text-sm font-bold uppercase text-gray-400 dark:text-gray-500">Members ({team.members?.length || 0})</h5>
                {team.members?.length > 0 ? team.members.map(mem => (
                    <div key={mem.id} className="text-base text-navy-700 dark:text-white bg-white dark:bg-navy-700 p-2 rounded-md shadow-sm">{mem.firstName} {mem.lastName} <span className="font-semibold text-brand-500">({mem.teamRole})</span></div>
                )) : <p className="text-base text-gray-400 text-center py-4">Drag students here</p>}
            </div>
        </div>
    );
};

const NewTeamDropzone = ({ onDrop }) => {
    const [{ isOver, canDrop }, drop] = useDrop(() => ({
        accept: ItemTypes.STUDENT,
        drop: (item) => onDrop(item.student),
        collect: (monitor) => ({
            isOver: !!monitor.isOver(),
            canDrop: !!monitor.canDrop(),
        }),
    }));

    return (
        <div
            ref={drop}
            className={`p-6 flex items-center justify-center text-center rounded-xl border-2 border-dashed transition-all cursor-pointer ${isOver && canDrop
                ? 'border-brand-500 bg-brand-500/10 text-brand-500'
                : 'border-gray-400 dark:border-navy-500 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-navy-700'
                }`}
            onClick={() => onDrop(null)}
        >
            <div>
                <p className="font-semibold text-lg">{isOver && canDrop ? 'Drop Student to Create Team' : '+ Create a New Team'}</p>
            </div>
        </div>
    );
};

const Pagination = ({ itemsPerPage, totalItems, paginate, currentPage }) => {
    const pageNumbers = [];
    const totalPages = Math.ceil(totalItems / itemsPerPage);

    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    if (totalPages <= 1) {
        return null;
    }

    return (
        <nav>
            <ul className="inline-flex items-center -space-x-px">
                <li>
                    <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="flex items-center justify-center px-3 h-8 ml-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-l-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-navy-800 dark:border-navy-700 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white"
                    >
                        <FiChevronLeft />
                    </button>
                </li>
                {pageNumbers.map(number => (
                    <li key={number}>
                        <button
                            onClick={() => paginate(number)}
                            className={`flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 dark:border-navy-700 ${currentPage === number
                                ? 'text-orange-600 bg-gray-50 dark:bg-navy-900 dark:text-orange-300'
                                : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700 dark:bg-navy-800 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white'
                                }`}
                        >
                            {number}
                        </button>
                    </li>
                ))}
                <li>
                    <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-r-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-navy-800 dark:border-navy-700 dark:text-gray-400 dark:hover:bg-navy-700 dark:hover:text-white"
                    >
                        <FiChevronRight />
                    </button>
                </li>
            </ul>
        </nav>
    );
};

const StudentTableSkeleton = () => (
    <tbody>
        <tr>
            <td colSpan="4" className="p-8 text-center text-gray-500 dark:text-gray-400">
                <FaUsers className="mx-auto text-4xl mb-2" />
                <p className="font-semibold">No Students Found</p>
                <p className="text-sm">Add students to this classroom to get started.</p>
            </td>
        </tr>
    </tbody>
);

const TeamBuilderSkeleton = () => (
    <div className="p-10 text-center text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-navy-600 rounded-xl">
        <MdGroup className="mx-auto text-5xl mb-3" />
        <h3 className="text-xl font-bold text-navy-700 dark:text-white">No Teams Yet</h3>
        <p>Create your first team or drag a student to the "Create New Team" area.</p>
    </div>
);


const ClassroomDetails = ({ id }) => {
    const { loading, error, classroom, students, teams,
        studentTeamMap, unassignedStudents, fetchTeams,
        handleDeleteStudent, handleAddStudent, handleCreateTeam, handleAssignStudent
    } = useClassroomManager(id);

    const [activeWorkspaceTab, setActiveWorkspaceTab] = useState("students");
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const STUDENTS_PER_PAGE = 10;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newStudentName, setNewStudentName] = useState("");
    const [newStudentUsername, setNewStudentUsername] = useState("");
    const [newStudentUsernameError, setNewStudentUsernameError] = useState("");
    const [addingStudent, setAddingStudent] = useState(false);

    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    const [teamData, setTeamData] = useState({ name: "", description: "" });
    const [teamNameError, setTeamNameError] = useState("");
    const [creatingTeam, setCreatingTeam] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [noUserFound, setNoUserFound] = useState(false);
    const [assignedRoles, setAssignedRoles] = useState([]);
    const [selectedRoles, setSelectedRoles] = useState({});
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [errorMessage, setErrorMessage] = useState("");

    const [isAssignRoleModalOpen, setIsAssignRoleModalOpen] = useState(false);
    const [assignmentData, setAssignmentData] = useState({ studentId: null, teamId: null, studentName: '', teamName: '', availableRoles: [] });
    const [selectedRoleForAssignment, setSelectedRoleForAssignment] = useState('');

    const unassignedStudentsContainerRef = useRef(null);
    const teamMembersRef = useRef(null);

    useEffect(() => {
        if (activeWorkspaceTab === 'builder') {
            fetchTeams();
        }
    }, [activeWorkspaceTab, fetchTeams]);

    // FIXED: This logic now finds the first member without a role, making the prompt persistent.
    const firstMemberWithoutRole = useMemo(() => {
        return selectedMembers.find(member => !member.role);
    }, [selectedMembers]);


    // Modified handleAddStudentSubmit (from my changes)
    const handleAddStudentSubmit = async (e) => {
        e.preventDefault();
        
        // Clear previous errors
        setNewStudentUsernameError("");
        
        // Validate username format
        if (!newStudentUsername.trim()) {
            setNewStudentUsernameError("Username is required.");
            return;
        }
        
        if (newStudentUsername.trim().length < 5) {
            setNewStudentUsernameError("Username must be at least 5 characters long.");
            return;
        }
        
        if (newStudentUsername.trim().length > 13) {
            setNewStudentUsernameError("Username must be no more than 13 characters long.");
            return;
        }
        
        if (!/^[a-zA-Z0-9]+$/.test(newStudentUsername.trim())) {
            setNewStudentUsernameError("Username must contain only letters and numbers.");
            return;
        }
        
        if (!newStudentName.trim()) {
            fireSwal({ title: 'Missing Information', text: 'Please provide a full name.', icon: 'warning' });
            return;
        }
        
        setAddingStudent(true);
        try {
            const studentData = {
                fullName: newStudentName,
                username: newStudentUsername.trim(),
                school: classroom.schoolName // Automatically set school to classroom's schoolName
            };
            const { success } = await handleAddStudent(studentData.fullName, studentData.username, studentData.school);
            if (success) {
                setIsModalOpen(false);
                setNewStudentName("");
                setNewStudentUsername("");
                setNewStudentUsernameError("");
            }
        } catch (error) {
            console.error('Error adding student:', error);
            
            // Handle specific validation errors
            if (error.response?.data?.field === 'username') {
                setNewStudentUsernameError(error.response.data.validation || error.response.data.message);
            } else {
                fireSwal({ 
                    title: 'Error', 
                    text: error.response?.data?.message || 'Failed to add student. Please try again.', 
                    icon: 'error' 
                });
            }
        } finally {
            setAddingStudent(false);
        }
    };

    const handleCreateTeamSubmit = async (e) => {
        e.preventDefault();
        if (!teamData.name) { setTeamNameError("Please provide a team name."); return; }
        if (selectedMembers.length < MIN_MEMBERS || selectedMembers.length > MAX_MEMBERS) {
            fireSwal({ title: 'Incorrect Team Size', text: `You must have between ${MIN_MEMBERS} and ${MAX_MEMBERS} members.`, icon: 'warning' });
            return;
        }
        setCreatingTeam(true);
        const { success } = await handleCreateTeam({
            name: teamData.name,
            description: teamData.description,
            classroomId: id,
            members: selectedMembers.map((m) => ({ id: m.id, role: m.role })),
        });
        if (success) {
            setIsTeamModalOpen(false);
            setTeamNameError("");
        }
        setCreatingTeam(false);
    };

    const handleAssignStudentSubmit = async () => {
        if (!assignmentData.studentId || !assignmentData.teamId || !selectedRoleForAssignment) return;
        await handleAssignStudent(assignmentData.studentId, assignmentData.teamId, selectedRoleForAssignment);
        setIsAssignRoleModalOpen(false);
    };

    const handleNewTeamDrop = (student) => {
        setTeamData({ name: "", description: "" });
        setSelectedRoles({});
        setAssignedRoles([]);
        setErrorMessage("");
        setSearchQuery("");
        setSearchResults([]);
        if (student) {
            if (studentTeamMap.has(student.id)) {
                fireSwal({ title: 'Already Assigned', text: 'This student is already in a team.', icon: 'warning' });
                return;
            }
            setSelectedMembers([{ ...student, role: '' }]);
        } else {
            setSelectedMembers([]);
        }
        setIsTeamModalOpen(true);
    };

    const onDropStudent = (studentId, teamId) => {
        if (studentTeamMap.has(studentId)) {
            fireSwal({ title: 'Already Assigned', text: 'This student is already in a team.', icon: 'warning' });
            return;
        }
        const student = students.find(s => s.id === studentId);
        const team = teams.find(t => t.id === teamId);
        if (!student || !team) return;

        const assignedRolesInTeam = team.members?.map(m => m.teamRole) || [];
        const availableRoles = roles.filter(r => !assignedRolesInTeam.includes(r));
        if (availableRoles.length === 0) {
            fireSwal({ title: 'Team Full', text: `This team has no available roles.`, icon: 'error', actionType: 'delete' });
            return;
        }
        setAssignmentData({ studentId, teamId, studentName: `${student.firstName} ${student.lastName}`, teamName: team.name, availableRoles });
        setSelectedRoleForAssignment(availableRoles[0] || '');
        setIsAssignRoleModalOpen(true);
    };

    const handleRoleChange = (userId, newRole) => {
        const currentMember = selectedMembers.find(m => m.id === userId);
        const oldRole = currentMember ? currentMember.role : undefined;
        setSelectedRoles((prev) => ({ ...prev, [userId]: newRole }));
        setSelectedMembers((prev) => prev.map((m) => (m.id === userId ? { ...m, role: newRole } : m)));
        setAssignedRoles(prev => {
            const updatedRoles = oldRole ? prev.filter(r => r !== oldRole) : prev;
            if (newRole && !updatedRoles.includes(newRole)) {
                updatedRoles.push(newRole);
            }
            return updatedRoles;
        });
    };

    const handleAddMember = (user) => {
        if (!user.isSubscribed) { setErrorMessage("This user is not subscribed."); return; }
        if (selectedMembers.length >= MAX_MEMBERS) { setErrorMessage(`You can only add up to ${MAX_MEMBERS} members.`); return; }
        if (!selectedRoles[user.id]) { setErrorMessage("Please select a role before adding the member."); return; }
        if (selectedMembers.some(m => m.id === user.id)) { setErrorMessage("This member is already selected."); return; }
        setSelectedMembers((prev) => [...prev, { ...user, role: selectedRoles[user.id] }]);
        setAssignedRoles((prev) => [...prev, selectedRoles[user.id]]);
        setErrorMessage("");
    };

    const handleRemoveMember = (userId) => {
        const removed = selectedMembers.find((m) => m.id === userId);
        setSelectedMembers((prev) => prev.filter((m) => m.id !== userId));
        if (removed?.role) {
            setAssignedRoles((prev) => prev.filter((role) => role !== removed.role));
        }
        setSelectedRoles((prev) => {
            const copy = { ...prev };
            delete copy[userId];
            return copy;
        });
    };

    const handleSearchChange = async (query) => {
        setSearchQuery(query);
        if (!query.trim()) {
            setSearchResults([]);
            setSearchLoading(false);
            return;
        }
        setSearchLoading(true);
        setNoUserFound(false);
        const filtered = students.filter((s) => s.username.toLowerCase().includes(query.toLowerCase()) && !studentTeamMap.has(s.id));
        if (filtered.length === 0) {
            setSearchResults([]);
            setNoUserFound(true);
            setSearchLoading(false);
            return;
        }
        try {
            const userIds = filtered.map((u) => u.id);
            const eligibility = await checkSubscriptionEligibility(userIds);
            const eligibilityMap = Object.fromEntries(eligibility.map(item => [item.userId, item.isEligible]));
            const updated = filtered.map((user) => ({ ...user, isSubscribed: eligibilityMap[user.id] || false }));
            setSearchResults(updated);
        } catch (error) {
            console.error("Error checking subscription eligibility", error);
        } finally {
            setSearchLoading(false);
        }
    };

    const getImageUrl = (url) => url || "/default-avatar.png";

    let studentsToDisplay;
    if (students.length > 10) {
        const indexOfLastStudent = currentPage * STUDENTS_PER_PAGE;
        const indexOfFirstStudent = indexOfLastStudent - STUDENTS_PER_PAGE;
        studentsToDisplay = students.slice(indexOfFirstStudent, indexOfLastStudent);
    } else if (students.length > 5) {
        studentsToDisplay = isExpanded ? students.slice(0, 10) : students.slice(0, 5);
    } else {
        studentsToDisplay = students;
    }
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) return <div className="p-10 text-center text-lg dark:text-white">Loading Workspace...</div>;
    if (error) return <div className="p-10 text-center text-red-500">{error}</div>;
    if (!classroom) return <div className="p-10 text-center dark:text-white">Classroom data not available.</div>;

    return (
        <DndProvider backend={HTML5Backend}>
            <div className="classroom-container space-y-4">
                <div className="border-b pb-4 dark:border-navy-600 flex justify-between items-center">
                    <nav className="flex space-x-8" aria-label="Tabs">
                        <button onClick={() => setActiveWorkspaceTab('students')} className={`px-1 pb-3 font-semibold text-md transition-colors ${activeWorkspaceTab === 'students' ? 'border-b-4 border-brand-500 text-brand-500' : 'text-gray-500 dark:text-gray-400 hover:text-brand-500'}`}>Student Management</button>
                        <button onClick={() => setActiveWorkspaceTab('builder')} className={`px-1 pb-3 font-semibold text-md transition-colors ${activeWorkspaceTab === 'builder' ? 'border-b-4 border-brand-500 text-brand-500' : 'text-gray-500 dark:text-gray-400 hover:text-brand-500'}`}>Team Builder</button>
                    </nav>
                </div>

                <div className="mt-6">
                    {activeWorkspaceTab === 'students' && (
                        <div className="bg-white dark:bg-navy-800 p-6 rounded-xl shadow-lg">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                                <div className="mb-4 sm:mb-0">
                                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Student Roster</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">A list of all students in the classroom.</p>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                >
                                    <IoPersonAddOutline size={20} />
                                    <span>Add New Student</span>
                                </button>
                            </div>
                            <div className="overflow-x-auto border border-gray-200 dark:border-navy-700 rounded-lg">
                                <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
                                    <thead className="text-xs text-gray-800 dark:text-gray-300 uppercase bg-gray-100 dark:bg-navy-700/50">
                                        <tr>
                                            <th scope="col" className="px-6 py-3 font-bold tracking-wider">Student Name</th>
                                            <th scope="col" className="px-6 py-3 font-bold tracking-wider">Username</th>
                                            <th scope="col" className="px-6 py-3 font-bold tracking-wider">Team Status</th>
                                            <th scope="col" className="px-6 py-3 font-bold tracking-wider text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    {students.length > 0 ? (
                                        <tbody>
                                            {studentsToDisplay.map((student) => (
                                                <tr key={student.id} className="bg-white dark:bg-navy-800 border-b dark:border-navy-700 hover:bg-gray-50/50 dark:hover:bg-navy-700/70 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="flex items-center gap-3">
                                                            <div className="h-10 w-10 flex-shrink-0">
                                                                {student.imageUrl ? (
                                                                    <img className="h-full w-full rounded-full object-cover" src={student.imageUrl} alt="Avatar" />
                                                                ) : (
                                                                    <div className="h-full w-full rounded-full bg-gray-100 dark:bg-gray-500/30 flex items-center justify-center">
                                                                        <span className="text-lg font-bold text-orange-500 dark:text-orange-300">{student.firstName?.charAt(0).toUpperCase()}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="font-medium text-gray-900 dark:text-white">{student.firstName} {student.lastName}</div>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-gray-600 dark:text-gray-300">@{student.username}</td>
                                                    <td className="px-6 py-4">
                                                        {studentTeamMap.has(student.id) ? (
                                                            <span className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold leading-5 text-green-800 bg-green-100 dark:bg-green-900/50 dark:text-green-300 rounded-full">
                                                                <MdGroup />{studentTeamMap.get(student.id)}
                                                            </span>
                                                        ) : (
                                                            <span className="px-3 py-1 text-xs font-semibold leading-5 text-gray-800 bg-gray-100 dark:bg-gray-500/20 dark:text-gray-300 rounded-full">Unassigned</span>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-right">
                                                        <button onClick={() => handleDeleteStudent(student.id)} className="p-2 text-gray-400 rounded-full hover:text-red-500 hover:bg-red-500/10 transition-colors" aria-label="Delete student">
                                                            <FaTrash />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    ) : (
                                        <StudentTableSkeleton />
                                    )}
                                </table>
                            </div>
                            <div className="mt-6 flex justify-center">
                                {students.length > 5 && students.length <= 10 && (
                                    <button onClick={() => setIsExpanded(!isExpanded)} className="flex items-center gap-2 font-semibold text-orange-500 hover:text-orange-600 dark:text-orange-400 dark:hover:text-orange-300 transition-colors">
                                        {isExpanded ? (<><span>Show Less</span><FiChevronUp /></>) : (<><span>Show More</span><FiChevronDown /></>)}
                                    </button>
                                )}
                                {students.length > 10 && (
                                    <Pagination itemsPerPage={STUDENTS_PER_PAGE} totalItems={students.length} paginate={paginate} currentPage={currentPage} />
                                )}
                            </div>
                        </div>
                    )}

                    {activeWorkspaceTab === 'builder' && (
                        <div className="flex flex-col lg:flex-row gap-8">
                            <div className="w-full lg:w-[65%] space-y-6">
                                {teams.length > 0 ? (
                                    teams.map(team => <DroppableTeamCard key={team.id} team={team} onDropStudent={onDropStudent} />)
                                ) : (
                                    <TeamBuilderSkeleton />
                                )}
                                <NewTeamDropzone onDrop={handleNewTeamDrop} />
                            </div>
                            <div className="w-full lg:w-[35%] bg-gray-100 dark:bg-navy-900/50 rounded-xl p-6 self-start">
                                <h4 className="font-bold text-xl mb-4 text-navy-700 dark:text-white">Unassigned Students ({unassignedStudents.length})</h4>
                                <div ref={unassignedStudentsContainerRef} className="space-y-3 max-h-[60vh] overflow-y-auto">
                                    {unassignedStudents.length > 0 ? (
                                        unassignedStudents.map(student => (
                                            <DraggableStudentCard key={student.id} student={student} scrollableContainerRef={unassignedStudentsContainerRef} />
                                        ))
                                    ) : (
                                        <div className="text-center p-4 text-gray-500 dark:text-gray-400">
                                            <p>All students have been assigned to a team.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {isTeamModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                        <div className="max-h-[90vh] overflow-y-auto bg-white dark:bg-navy-800 rounded-xl w-full max-w-5xl p-6 md:p-8 shadow-xl relative">
                            <h2 className="text-2xl font-bold text-navy-700 dark:text-white mb-6 flex items-center">
                                <MdGroup className="text-3xl mr-2" />Create New Team
                            </h2>
                            <form onSubmit={handleCreateTeamSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                    <div className="space-y-6">
                                        <InputField extra="w-full" label="Team Name" placeholder="e.g., The Innovators" id="name" type="text" value={teamData.name || ""} onChange={(e) => setTeamData(prev => ({ ...prev, name: e.target.value }))} icon={<MdGroup className="text-2xl" />} error={teamNameError} state={teamNameError ? "error" : ""} />
                                        <InputField extra="w-full" label="School Name" id="schoolName" type="text" value={classroom?.name || ""} icon={<FaSchool className="text-2xl" />} readOnly />
                                    </div>
                                    <div className="space-y-6">
                                        <TextField extra="w-full" label="Description" placeholder="Short description about the team!" id="description" value={teamData.description || ""} onChange={(e) => setTeamData(prev => ({ ...prev, description: e.target.value }))} cols="30" rows="8" icon={<AiOutlineInfoCircle className="text-2xl" />} />
                                    </div>
                                </div>
                                <div>
                                    <h4 className="flex items-center gap-2 mb-4 text-xl font-bold text-navy-700 dark:text-white"><IoPersonAddOutline className="text-2xl" /> Add Members</h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Add between {MIN_MEMBERS} and {MAX_MEMBERS} members.</p>
                                    {firstMemberWithoutRole && (
                                        <div className="mb-4 p-3 flex items-center text-black-900 dark:text-blue-300 bg-orange-100 dark:bg-blue-900/50 rounded-lg">
                                            <MdErrorOutline className="mr-2 text-xl" />
                                            <p className="text-sm font-semibold">Please select a role for {firstMemberWithoutRole.firstName} to continue.</p>
                                        </div>
                                    )}
                                    {errorMessage && (
                                        <div className="mb-4 p-4 flex items-center text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/80 rounded-lg">
                                            <IoAlertCircle className="mr-2 text-xl" /><p className="text-sm">{errorMessage}</p>
                                        </div>
                                    )}
                                    <InputField extra="w-full mb-6" label="Search Member by Username" placeholder="e.g., johndoe" id="searchmember" type="text" value={searchQuery} onChange={(e) => handleSearchChange(e.target.value)} icon={<IoSearchOutline className="text-xl" />} />
                                    {searchLoading && <div className="text-gray-500 dark:text-gray-300 mb-4">Searching...</div>}
                                    {searchResults.length > 0 && (
                                        <div className="mb-4 space-y-2">
                                            {searchResults.map((user) => {
                                                const teamName = studentTeamMap.get(user.id);
                                                const isAlreadyInTeam = !!teamName;
                                                const isSelected = selectedMembers.some((m) => m.id === user.id);
                                                const isAddButtonDisabled = !user.isSubscribed || isSelected || isAlreadyInTeam;
                                                const tooltipMessage = isAlreadyInTeam ? `Already in team: ${teamName}` : !user.isSubscribed ? "This user is not subscribed." : isSelected ? "User is already added to this team." : !selectedRoles[user.id] ? "Please select a role before adding." : "";
                                                return (
                                                    <div key={user.id} className={`flex flex-col md:flex-row items-start md:items-center justify-between p-4 rounded-lg bg-gray-50 dark:bg-navy-700 ${isSelected ? "opacity-50" : "hover:bg-gray-100 dark:hover:bg-navy-600"}`}>
                                                        <div className="flex items-center flex-1 min-w-0">
                                                            <img src={getImageUrl(user.imageUrl)} alt={`${user.firstName} ${user.lastName}`} className="w-12 h-12 rounded-full object-cover mr-4 shadow" />
                                                            <div className="truncate">
                                                                <p className="font-medium text-navy-700 dark:text-white">{user.firstName} {user.lastName}</p>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">@{user.username}</p>
                                                                {isAlreadyInTeam ? (<span className="text-xs font-semibold inline-flex items-center mt-1 px-2 py-0.5 rounded bg-yellow-100 text-yellow-700"><IoShieldCheckmarkOutline className="mr-1" />In team: {teamName}</span>) : (<span className={`text-xs font-semibold inline-block mt-1 px-2 py-0.5 rounded ${user.isSubscribed ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"}`}>{user.isSubscribed ? "Subscribed" : "Not Subscribed"}</span>)}
                                                            </div>
                                                        </div>
                                                        <div className="mt-3 md:mt-0 flex flex-col sm:flex-row items-stretch gap-2 w-full md:w-auto">
                                                            <select value={selectedRoles[user.id] || ""} onChange={(e) => handleRoleChange(user.id, e.target.value)} disabled={isAddButtonDisabled} className="rounded-xl border dark:border-white/10 bg-white dark:bg-navy-900 text-navy-700 dark:text-white text-sm px-4 py-2">
                                                                <option value="" disabled>Select Role</option>
                                                                {roles.filter((role) => !assignedRoles.includes(role) || selectedRoles[user.id] === role).map((role) => (<option key={role} value={role}>{role}</option>))}
                                                            </select>
                                                            <div className="relative group">
                                                                <button type="button" onClick={() => handleAddMember(user)} disabled={isAddButtonDisabled || !selectedRoles[user.id]} className={`w-full sm:w-auto px-3 py-2 text-sm rounded-xl ${!isAddButtonDisabled && selectedRoles[user.id] ? "bg-orange-500 text-white hover:bg-orange-600" : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}>
                                                                    <IoPersonAddOutline className="inline-block mr-1" />Add
                                                                </button>
                                                                {tooltipMessage && (<div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 px-2 py-1 text-xs bg-gray-800 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 w-max">{tooltipMessage}</div>)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                    {searchQuery && noUserFound && (<div className="mb-4 p-2 bg-red-100 dark:bg-red-900/80 rounded-md"><p className="text-red-500 dark:text-red-400">User not found</p></div>)}
                                    <div ref={teamMembersRef} className="space-y-4">
                                        <h5 className="text-lg font-bold text-navy-700 dark:text-white">Selected Members ({selectedMembers.length}/{MAX_MEMBERS})</h5>
                                        {selectedMembers.length === 0 ? (<p className="text-gray-500 dark:text-gray-400">No members selected.</p>) : (
                                            <ul className="space-y-2">
                                                {selectedMembers.map((member) => (
                                                    <li key={member.id} className="flex items-center justify-between p-2 bg-gray-100 dark:bg-navy-900 rounded-md">
                                                        <div className="flex items-center">
                                                            <img src={getImageUrl(member.imageUrl)} alt={`${member.firstName} ${member.lastName}`} className="w-10 h-10 rounded-full object-cover mr-4" />
                                                            <div>
                                                                <p className="font-medium text-navy-700 dark:text-white">{member.firstName} {member.lastName}</p>
                                                                <p className="text-sm text-gray-500 dark:text-gray-400">@{member.username}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <select value={member.role} onChange={(e) => handleRoleChange(member.id, e.target.value)} className="bg-white dark:bg-navy-700 border dark:border-navy-600 rounded-lg px-3 py-1 text-sm text-navy-700 dark:text-white">
                                                                <option value="" disabled>Select Role</option>
                                                                {roles.filter((role) => !assignedRoles.includes(role) || member.role === role).map((role) => (<option key={role} value={role}>{role}</option>))}
                                                            </select>
                                                            <button type="button" onClick={() => handleRemoveMember(member.id)} className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-500/10"><IoPersonRemoveOutline className="text-xl" /></button>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                                <div className="mt-8 flex justify-end space-x-4">
                                    <button type="button" onClick={() => setIsTeamModalOpen(false)} className="px-4 py-2 rounded text-navy-700 dark:text-white border border-gray-300 dark:border-navy-600 hover:bg-gray-100 dark:hover:bg-navy-700">Cancel</button>
                                    <button type="submit" disabled={creatingTeam || selectedMembers.length < MIN_MEMBERS || selectedMembers.length > MAX_MEMBERS || selectedMembers.some(m => !m.role)} className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">{creatingTeam ? "Creating..." : "Create Team"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[100]">
                        <div className="bg-white dark:bg-navy-800 rounded-2xl p-8 w-full max-w-md shadow-2xl">
                            <h2 className="text-2xl font-bold mb-6 text-navy-700 dark:text-white">Add New Student</h2>
                            <form onSubmit={handleAddStudentSubmit} className="space-y-5">
                                <InputField label="Full Name" placeholder="e.g., Jane Doe" id="fullName" type="text" value={newStudentName} onChange={(e) => setNewStudentName(e.target.value)} />
                                <InputField label="Username" placeholder="e.g., janedoe99" id="username" type="text" value={newStudentUsername} onChange={(e) => { setNewStudentUsername(e.target.value); setNewStudentUsernameError(""); }} error={newStudentUsernameError} state={newStudentUsernameError ? "error" : ""} />
                                <div className="flex justify-end space-x-4 pt-4">
                                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-100 dark:border-navy-600 dark:hover:bg-navy-700 font-semibold text-base text-navy-700 dark:text-white">Cancel</button>
                                    <button type="submit" disabled={addingStudent} className="px-6 py-3 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 font-semibold text-base">{addingStudent ? "Adding..." : "Add Student"}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {isAssignRoleModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-[100] p-4">
                        <div className="bg-white dark:bg-navy-800 rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-xl border border-gray-200 dark:border-navy-700">
                            <div className="pb-4 border-b border-gray-200 dark:border-navy-700">
                                <h2 className="text-2xl font-bold text-navy-800 dark:text-white tracking-tight">Assign Role</h2>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    Assign <span className="font-semibold text-navy-700 dark:text-white">{assignmentData.studentName}</span> to team <span className="font-semibold text-navy-700 dark:text-white">{assignmentData.teamName}</span>
                                </p>
                            </div>
                            <div className="mt-6 space-y-6">
                                <div>
                                    <label htmlFor="role-select" className="block text-sm font-medium mb-2 text-navy-700 dark:text-gray-300">Select an available role</label>
                                    <div className="relative">
                                        <select
                                            id="role-select"
                                            value={selectedRoleForAssignment}
                                            onChange={(e) => setSelectedRoleForAssignment(e.target.value)}
                                            className="appearance-none w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-navy-700 border-gray-300 dark:border-navy-600 text-navy-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
                                        >
                                            {assignmentData.availableRoles.map(role => (<option key={role} value={role}>{role}</option>))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-gray-300">
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-end space-x-4 pt-4">
                                    <button type="button" onClick={() => setIsAssignRoleModalOpen(false)} className="px-6 py-2.5 rounded-lg border border-gray-300 dark:border-navy-600 font-semibold text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-navy-700 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:focus:ring-navy-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800 transition-colors">Cancel</button>
                                    <button type="button" onClick={handleAssignStudentSubmit} className="px-6 py-2.5 rounded-lg bg-brand-500 text-white font-semibold text-sm hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 dark:focus:ring-offset-navy-800 transition-colors">Assign Student</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DndProvider>
    );
};

export default ClassroomDetails;